/**
 * One message in a thread.
 *
 * The body itself - sanitising, the quoted-chain fold, HTML versus plain text -
 * lives in `MailBody`, shared with the pane that reads mail straight off the
 * IMAP server. Outbound is the CRM's own HTML and is trusted with its inline
 * styling; inbound is a stranger's and is not.
 *
 * A failed message also prints the transport's own error. The row has carried
 * one since the mailbox shipped, but only the badge was drawn, so a delivery
 * failure read as "the CRM is broken": the operator saw a red "Failed" on a
 * mailbox whose password was demonstrably fine, with the sentence that named
 * the real cause sitting unread in the database. It is server text, not sender
 * text, so it goes in as a plain string.
 */
import { cn } from "@wa/ui";
import { attachmentUrl, type MailMessage } from "../../../lib/mail";
import { AttachmentChips } from "../_shared/attachments";
import { useWorkCompact } from "../_shared/density";
import { MailBody } from "./MailBody";
import { fmtDateTime, type T } from "./shared";

export function MessageBubble({ t, m }: { t: T; m: MailMessage }) {
  const outbound = m.direction === "outbound";
  // Swapped rather than overridden: these are Tailwind utilities, and a utility
  // beats anything the density stylesheet could say about the same element.
  const compact = useWorkCompact();
  return (
    <div
      className={cn(
        "border",
        compact ? "rounded-lg p-2.5" : "rounded-xl p-4",
        outbound ? "border-primary/30 bg-primary/5" : "border-border bg-muted/30",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2 text-xs text-muted-foreground",
          compact ? "mb-1" : "mb-1.5",
        )}
      >
        {/* `min-w-0` and a break rule, because an address is one unbreakable
            word. Without them this flex item cannot shrink below its longest
            run and it pushed the timestamp beside it off the bubble - which on
            a narrow pane is off the card, since the card clips. */}
        <span className="min-w-0 font-medium text-foreground [overflow-wrap:anywhere]">
          {m.fromName ? `${m.fromName} ` : ""}&lt;{m.fromAddress}&gt;
          <span className="ml-1.5 text-muted-foreground">→ {m.toAddresses.join(", ")}</span>
          {/* The copies, read the same way the mail-server pane prints them.
              A blind copy is on our own sent letters only, and this line is
              the one place anybody can see who got one. */}
          {m.ccAddresses.length ? (
            <span className="ml-1.5 text-muted-foreground">· {t("email.cc")}: {m.ccAddresses.join(", ")}</span>
          ) : null}
          {m.bccAddresses?.length ? (
            <span className="ml-1.5 text-muted-foreground">· {t("email.bcc")}: {m.bccAddresses.join(", ")}</span>
          ) : null}
        </span>
        <div className="flex items-center gap-2">
          {outbound && m.status !== "sent" && (
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-medium",
                m.status === "failed"
                  ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
              )}
            >
              {t(`email.msgStatus.${m.status}`)}
            </span>
          )}
          <span className="tabular-nums">{fmtDateTime(m.createdAt)}</span>
        </div>
      </div>
      <MailBody t={t} html={m.bodyHtml} text={m.bodyText ?? m.snippet} trusted={outbound} />
      {outbound && m.status === "failed" && m.error && (
        <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-2">
          <p className="text-[11px] font-medium text-red-600 dark:text-red-300">{t("email.sendErrorLabel")}</p>
          <p className="mt-0.5 break-words font-mono text-[11px] leading-snug text-red-600/90 dark:text-red-300/90">
            {m.error}
          </p>
        </div>
      )}
      {m.attachments && m.attachments.length > 0 && (
        <AttachmentChips
          t={t}
          items={m.attachments}
          // By index into this message's own list. `stored` is the server's one
          // bit of truth about whether there are bytes behind the record - a
          // message from before the mailbox kept them has the name and nothing
          // else, and the chip says so rather than offering a dead link.
          href={(i, a) => (a.stored ? attachmentUrl(m.id, i) : null)}
        />
      )}
      {outbound && m.status === "dev" && <p className="mt-1 text-[11px] text-amber-600">{t("email.devNotice")}</p>}
    </div>
  );
}
